import { useState } from 'react';
import { Edit2, Trash2, Plus } from 'lucide-react';
import { TrainingCamp } from '../../lib/types';
import { TrainingCampModal } from './TrainingCampModal';

interface TrainingCampsSectionProps {
  camps: TrainingCamp[];
  onOrderChange: (campId: string, newOrder: number) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onSave: (camp: Partial<TrainingCamp>, file: File | null) => Promise<void>;
}

export function TrainingCampsSection({
  camps,
  onOrderChange,
  onDelete,
  onSave,
}: TrainingCampsSectionProps) {
  const [editingCamp, setEditingCamp] = useState<TrainingCamp | null>(null);
  const [isAddingCamp, setIsAddingCamp] = useState(false);

  return (
    <div className="mt-12">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Training Camps</h2>
        <button
          onClick={() => setIsAddingCamp(true)}
          className="bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 flex items-center gap-2"
        >
          <Plus size={20} />
          Add New Camp
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="space-y-4">
          {camps.map((camp) => (
            <div
              key={camp.id}
              className="border rounded-lg p-4 bg-gray-50"
            >
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-16">
                  <input
                    type="number"
                    value={camp.order}
                    onChange={(e) => onOrderChange(camp.id, parseInt(e.target.value))}
                    className="w-full px-2 py-1 border rounded text-center"
                    min="0"
                    max={camps.length - 1}
                  />
                </div>
                <img
                  src={camp.imageUrl}
                  alt={camp.title}
                  className="w-20 h-20 object-cover rounded-lg"
                />
                <div className="flex-grow">
                  <h3 className="font-semibold">{camp.title}</h3>
                  <p className="text-gray-600">{camp.location}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(camp.startDate).toLocaleDateString()} - {new Date(camp.endDate).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingCamp(camp)}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    <Edit2 size={20} />
                  </button>
                  <button
                    onClick={() => onDelete(camp.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {(isAddingCamp || editingCamp) && (
        <TrainingCampModal
          camp={editingCamp || undefined}
          onClose={() => {
            setEditingCamp(null);
            setIsAddingCamp(false);
          }}
          onSave={onSave}
        />
      )}
    </div>
  );
}